import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Check for token in URL params (from LinkedIn callback)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      
      if (urlToken) {
        // Clear token from URL immediately
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // LinkedIn OAuth always creates expert accounts - no need to detect
        localStorage.setItem('collabhive_token', urlToken);
        localStorage.setItem('collabhive_user_type', 'expert');
        
        setToken(urlToken);
        setUserType('expert');
        await fetchUser(urlToken, 'expert');

        // If user came from a shareable opportunity link, send them back after auth
        try {
          const redirectPath = localStorage.getItem('post_auth_redirect');
          if (redirectPath && redirectPath.startsWith('/opportunities/')) {
            localStorage.removeItem('post_auth_redirect');
            window.location.replace(redirectPath);
            return;
          }
        } catch (e) { /* noop */ }
        return;
      }

      // Load token from localStorage
      const storedToken = localStorage.getItem('collabhive_token');
      const storedUserType = localStorage.getItem('collabhive_user_type');
      
      if (storedToken && storedUserType) {
        setToken(storedToken);
        setUserType(storedUserType);
        await fetchUser(storedToken, storedUserType);
      } else {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);

  const fetchUser = async (authToken, type) => {
    try {
      const endpoint = type === 'expert' ? '/experts/me' : '/industry/me';
      const response = await fetch(`${API}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setUserType(type);
      } else {
        // Token invalid, clear storage
        logout();
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const loginExpert = (authToken) => {
    localStorage.setItem('collabhive_token', authToken);
    localStorage.setItem('collabhive_user_type', 'expert');
    setToken(authToken);
    setUserType('expert');
    fetchUser(authToken, 'expert');
  };

  const loginIndustry = (authToken, userId) => {
    localStorage.setItem('collabhive_token', authToken);
    localStorage.setItem('collabhive_user_type', 'industry');
    localStorage.setItem('collabhive_user_id', userId);
    setToken(authToken);
    setUserType('industry');
    fetchUser(authToken, 'industry');
  };

  const logout = () => {
    localStorage.removeItem('collabhive_token');
    localStorage.removeItem('collabhive_user_type');
    localStorage.removeItem('collabhive_user_id');
    setUser(null);
    setToken(null);
    setUserType(null);
  };

  const value = {
    user,
    token,
    userType,
    loading,
    isAuthenticated: !!token,
    isExpert: userType === 'expert',
    isIndustry: userType === 'industry',
    loginExpert,
    loginIndustry,
    logout,
    refreshUser: () => token && fetchUser(token, userType),
    API
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
